import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";

import AdSlot from "../components/ad-slot";
import ArticleCard from "../components/article-card";
import SocialLinks from "../components/social-links";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { articles } from "../lib/articles";
import { usePageMeta } from "../lib/use-page-meta";

const topics = [
  {
    title: "Things I tinker with",
    description:
      "Small experiments, half-finished ideas, and notes from learning as I go.",
  },
  {
    title: "Jitra, Kedah",
    description:
      "The town I call home: local food, slow mornings, and small memories.",
  },
  {
    title: "Things I am learning",
    description:
      "Books, hobbies, and half-formed ideas worth writing down before they disappear.",
  },
];

function Home() {
  usePageMeta({});

  return (
    <>
      <section className="container grid items-center gap-12 overflow-hidden py-14 md:grid-cols-[1.15fr_0.85fr] md:py-20">
        <div>
          <Badge variant="secondary" className="gap-1.5 font-normal">
            <MapPin className="h-3.5 w-3.5" />
            Malaysia
          </Badge>

          <h1 className="mt-6 font-serif text-5xl font-semibold leading-[1.04] tracking-tight lg:text-6xl">
            Hi, I&apos;m Aiman.{" "}
            <span className="text-primary">I write about small discoveries</span> and
            the quiet parts of life.
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground">
            A small corner of the internet for notes on learning, everyday hobbies, and
            paying attention. No feed, no noise — just what feels worth keeping.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/articles">
                Read the articles <ArrowRight />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/about">About me</Link>
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-3">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Find me
            </span>
            <SocialLinks />
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-[220px] sm:max-w-[240px]">
          <div
            aria-hidden="true"
            className="absolute -inset-4 rotate-3 rounded-[2rem] bg-secondary"
          />
          <div
            aria-hidden="true"
            className="absolute -inset-4 -rotate-2 rounded-[2rem] border border-primary/25"
          />
          <img
            src="/assets/aiman-portrait.jpg"
            alt="Portrait of Aiman"
            width="800"
            height="800"
            className="relative aspect-square w-full rounded-[1.75rem] object-cover shadow-xl"
          />
          <div className="absolute -bottom-5 left-4 rounded-xl border bg-background px-4 py-3 shadow-lg">
            <p className="font-serif text-sm italic text-foreground/90">
              “Makan la sikit, nanti sakit.”
            </p>
          </div>
        </div>
      </section>

      <section className="border-y bg-card/50">
        <div className="container py-14">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                Latest writing
              </p>
              <h2 className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">
                New from the desk
              </h2>
            </div>
            <Button asChild variant="ghost">
              <Link to="/articles">
                All articles <ArrowRight />
              </Link>
            </Button>
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles.slice(0, 3).map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        </div>
      </section>

      <AdSlot />

      <section className="container pb-16">
        <div className="grid gap-6 md:grid-cols-3">
          {topics.map((topic) => (
            <div key={topic.title} className="rounded-lg border bg-card p-6">
              <h2 className="font-serif text-xl font-semibold tracking-tight">
                {topic.title}
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {topic.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 grid items-center gap-8 rounded-2xl border bg-secondary/50 p-8 md:grid-cols-[1fr_auto] md:p-12">
          <div>
            <h2 className="font-serif text-3xl font-semibold tracking-tight">
              A little about this place
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              I&apos;m Aiman. Jitra, Kedah is my hometown. This site is where I keep things I
              learn, small hobbies, and the occasional thought that deserves more than
              a post. I&apos;m not a professional developer — just curious and keeping
              notes.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/about">
              More about me <ArrowRight />
            </Link>
          </Button>
        </div>
      </section>
    </>
  );
}

export default Home;
